import { m } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LANDING } from '@/shared/motion/transitions';
import { mealsApi } from '@/shared/api/endpoints';
import type { Estimate, EstimateSource, Meal, MealItem, TraceStep } from '@/shared/api/types';
import { formatTime } from '@/shared/format/dates';
import {
  AI_ESTIMATE_ADVICE,
  formatEstimateKcal,
  formatGrams,
  isAiEstimate,
  isUncertain,
  itemStateLabel,
  itemTitle,
  mealTypeLabel,
  unitLabel,
} from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, ConfidenceMeter, Eyebrow, MacroGrid } from '@/shared/ui';
import { BackIcon, CameraIcon, CloseIcon } from '@/shared/ui/icons';
import { ItemThumb, useSignedImageUrl } from '@/shared/ui/MediaThumb';
import { EsqueletoDetalle, useEsqueleto } from '@/shared/ui/Skeleton';
import { CorrectItemSheet, type CorrectionMode } from './CorrectItemSheet';
import { EstimateHistory } from './EstimateHistory';

/** Cada cuánto se vuelve a leer la comida mientras el ítem sigue pendiente. */
const PENDING_POLL_INTERVAL_MS = 2500;

/** Cuántos fallos seguidos hacen falta antes de avisar. Uno solo no es una novedad. */
const FALLOS_ANTES_DE_AVISAR = 4;

/** Lo más espaciado que llega a preguntar una espera larga. */
const PENDING_POLL_MAX_MS = 15_000;


function intervaloDeEspera(vueltas: number): number {
  return Math.min(PENDING_POLL_INTERVAL_MS * 2 ** Math.floor(vueltas / 4), PENDING_POLL_MAX_MS);
}

/**
 * S-04 · el detalle del ítem. Es la pantalla que define si el producto es usable.
 *
 * La declaración y la estimación viven separadas y en ese orden: lo que la persona dijo es
 * inmutable (RN-01), y lo que el sistema calculó es una lectura sobre eso. Mezclarlas haría
 * parecer que corregir el número corrige lo que comió.
 */
export function ItemDetailPage() {
  const { mealId = '', itemId = '' } = useParams();
  const navigate = useNavigate();
  const loaded = useAsync<Meal>(() => mealsApi.get(mealId), [mealId]);

  const [meal, setMeal] = useState<Meal | null>(null);
  const [correcting, setCorrecting] = useState<CorrectionMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [borrando, setBorrando] = useState(false);

  /**
   * Borra el ítem y vuelve al día.
   *
   * Se vuelve a «Hoy» y no a la comida a propósito: si era el único ítem, la comida ya no tiene
   * nada que mostrar, y aterrizar en una pantalla vacía después de borrar se lee como si algo
   * hubiera salido mal.
   */
  const borrarItem = async () => {
    setBusy(true);
    try {
      await mealsApi.removeItem(mealId, itemId);
      navigate('/', { replace: true });
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
      setBorrando(false);
    }
  };
  const photoInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (loaded.data !== null) {
      setMeal(loaded.data);
    }
  }, [loaded.data]);

  const item = meal?.items.find((candidate) => candidate.id === itemId) ?? null;

  /*
   * RF-10 · cero o más. El héroe muestra la primera y la tira de abajo, todas.
   *
   * El héroe acepta CUALQUIER rol, no sólo `Dish`. Antes filtraba a plato, y por eso subir la foto
   * de un rótulo dejaba la pantalla sin imagen: sin imagen no había sobre qué dibujar el escaneo,
   * así que tampoco había animación. Quedaba «subido» y nada más, que es lo contrario de lo que
   * está pasando —el modelo está leyendo la foto en ese mismo momento—.
   *
   * La tira de abajo sí sigue siendo sólo de platos: son las fotos del alimento, y mezclar ahí el
   * rótulo confundiría dos cosas distintas.
   */
  const dishes = item?.media.filter((media) => media.role === 'Dish') ?? [];
  const hero = dishes[0] ?? item?.media[0] ?? null;
  const photoUrl = useSignedImageUrl(mealId, itemId, hero);
  const waiting = item?.state === 'Pending';

  /*
   * Mientras el ítem está pendiente, la pantalla vuelve a preguntar sola.
   *
   * CU-02 paso 6 resuelve fuera del request a propósito, y el registro navega acá con `replace`
   * apenas responde el POST. O sea que esta pantalla se abre SIEMPRE antes de que la estimación
   * exista. Sin esta relectura, la primera pantalla que ve alguien después de registrar una comida
   * se queda congelada en un estado que dejó de ser cierto dos segundos más tarde.
   *
   * Sigue hasta que el ítem deja de estar pendiente, y no treinta segundos como antes. Ese corte
   * partía de suponer que una resolución tarda poco; leer un rótulo con un modelo de visión puede
   * tardar bastante más, y cuando el corte llegaba primero la pantalla se quedaba quieta con el
   * ítem todavía pendiente — sin decirlo. Quien miraba veía una foto que nunca terminaba.
   *
   * El intervalo se abre a medida que pasa el tiempo en vez de tener un tope: los primeros
   * segundos se pregunta seguido, porque casi siempre resuelve ahí, y después cada vez menos. Así
   * una espera larga se acompaña sin que la pantalla golpee al servidor cien veces por minuto.
   *
   * Y como esto vive en un efecto atado al montaje, salir y volver a entrar retoma el sondeo solo:
   * es el mismo camino, no un caso aparte.
   */
  useEffect(() => {
    if (!waiting) {
      return;
    }

    let cancelado = false;
    let timer = 0;
    let vueltas = 0;
    let fallosSeguidos = 0;

    const preguntar = () => {
      mealsApi
        .get(mealId)
        .then((actualizada) => {
          if (cancelado) {
            return;
          }

          fallosSeguidos = 0;
          setError(null);
          setMeal(actualizada);
          vueltas += 1;
          timer = window.setTimeout(preguntar, intervaloDeEspera(vueltas));
        })
        .catch((cause: unknown) => {
          if (cancelado) {
            return;
          }

          /*
           * Un pedido que falla NO termina la espera.
           *
           * Antes el primer error cortaba el sondeo para siempre, y eso convertía cualquier
           * parpadeo —el ascensor, el subte, un despliegue del servidor— en una pantalla que se
           * queda en «Estimando» aunque la estimación ya esté lista del otro lado. Lo comprobé
           * reiniciando la API con la pantalla abierta: quedó colgada mientras el ítem ya decía
           * «Resolved».
           *
           * El aviso aparece recién tras varios fallos seguidos, porque uno solo no es una
           * novedad para nadie, y se borra apenas una respuesta vuelve a llegar.
           */
          fallosSeguidos += 1;

          if (fallosSeguidos >= FALLOS_ANTES_DE_AVISAR) {
            setError(describeError(cause));
          }

          vueltas += 1;
          timer = window.setTimeout(preguntar, intervaloDeEspera(vueltas));
        });
    };

    timer = window.setTimeout(preguntar, PENDING_POLL_INTERVAL_MS);

    return () => {
      cancelado = true;
      window.clearTimeout(timer);
    };
  }, [mealId, itemId, waiting]);

  // El esqueleto no se muestra apenas arranca la lectura: por debajo del umbral
  // aparecería y se iría antes de que el ojo lo registre, que es un parpadeo.
  const esqueleto = useEsqueleto(loaded.loading);

  /*
   * La condición de «todavía cargando» es `loaded.loading`, NO `esqueleto`.
   *
   * `esqueleto` arranca en false durante 260 ms para no parpadear en una carga rápida. Atar el
   * guard a él deja esos 260 ms sin cubrir: con la comida todavía en null, la pantalla cae al
   * banner de abajo y muestra «No se encontró este ítem» sobre un pedido que está en curso.
   * Reproducido con 900 ms de latencia: diez cuadros seguidos de error falso antes de que llegue
   * la respuesta.
   */
  if (loaded.loading && meal === null) {
    return esqueleto ? <EsqueletoDetalle /> : null;
  }

  if (meal === null || item === null) {
    return <Banner tone="danger">{loaded.error ?? 'No se encontró este ítem.'}</Banner>;
  }

  const run = async (action: () => Promise<Meal>) => {
    setBusy(true);
    setError(null);
    try {
      setMeal(await action());
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const estimate = item.currentEstimate;
  const hasLabelPhoto = item.media.some((media) => media.role === 'NutritionLabel');

  return (
    <>
      <div className="hero">
        {photoUrl === null ? (
          <div
            className="item-thumb-empty"
            style={{ height: 250, borderRadius: 0 }}
            aria-hidden="true"
          />
        ) : (
          <img src={photoUrl} alt={itemTitle(item, 'Foto del plato')} />
        )}
        <span className="hero-scrim" />
        {/* Mientras se estima, la foto «se escanea»: los destellos dicen que algo la está mirando. */}
        {waiting && photoUrl !== null && <ScanOverlay />}
        <div className="hero-actions">
          <button
            type="button"
            className="glass glass-square"
            aria-label="Volver"
            onClick={() => navigate(-1)}
          >
            <BackIcon size={18} />
          </button>
          <div className="row">
            <button
              type="button"
              className="glass glass-square"
              aria-label="Agregar una foto"
              onClick={() => photoInput.current?.click()}
            >
              <CameraIcon size={18} />
            </button>
            {photoUrl !== null && estimate?.source === 'Declaration' && (
              <button
                type="button"
                className="glass glass-square"
                aria-label="Reanalizar con la foto"
                disabled={busy || waiting}
                onClick={() => void run(() => mealsApi.resolveNow(mealId, itemId))}
              >
                ⟳
              </button>
            )}
            {hero !== null && (
              <button
                type="button"
                className="glass glass-square"
                aria-label="Quitar la foto"
                disabled={busy}
                onClick={() => void run(() => mealsApi.removeImage(mealId, itemId, hero.id))}
              >
                <CloseIcon size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="hero-lift">
        <Card size="lg">
          <div className="stack-tight stack">
            <Eyebrow>Lo que declaraste</Eyebrow>
            {/*
              Acá va lo que la persona declaró y nada más. Es tentador rellenar con la descripción
              que produjo el modelo —existe, y está a dos centímetros— pero RN-21 la guarda en la
              estimación justamente porque RN-01 prohíbe que una resolución escriba en la
              declaración. Se muestra abajo, junto al número, que es de donde salió.
            */}
            <strong style={{ fontSize: '1.0625rem', fontWeight: 800 }}>
              {item.freeText ?? item.ean ?? 'Sólo la foto'}
            </strong>
            <span className="micro muted numeric">
              {[
                item.quantity !== null && item.unit !== null
                  ? `${formatGrams(item.quantity)} ${unitLabel(item.unit)}`
                  : null,
                `${mealTypeLabel(meal.type).toLowerCase()} de ${formatTime(meal.occurredAt)}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>

          <hr className="divider" />

          <div className="row-between">
            <Eyebrow>Estimación vigente</Eyebrow>
            {estimate !== null && (
              <ConfidenceMeter
                source={estimate.source}
                confidence={estimate.confidence}
                size="detail"
              />
            )}
          </div>

          {estimate === null ? (
            <MissingEstimate
              item={item}
              busy={busy}
              hasLabelPhoto={hasLabelPhoto}
              onResolve={() => void run(() => mealsApi.resolveNow(mealId, itemId))}
              onPhoto={() => photoInput.current?.click()}
              onProduct={() => setCorrecting('product')}
              onLabel={() => navigate(`/comidas/${mealId}/items/${itemId}/rotulo`)}
            />
          ) : (
            <>
              {/*
                El único resorte de la aplicación, y se lo gana: es el instante en que llega la
                estimación que la persona estaba esperando, muchas veces mientras mira la pantalla.
                Un fundido lo trataría igual que a cualquier otro cambio de texto.

                La clave es el valor: si una corrección cambia el número, vuelve a aterrizar.
              */}
              <m.div
                key={estimate.kcal}
                className="figure figure-item"
                initial={{ opacity: 0, scale: 0.94, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={LANDING}
              >
                <span className={isUncertain(estimate) ? 'figure-value uncertain' : 'figure-value'}>
                  {formatEstimateKcal(estimate)}
                </span>
                <span className="figure-unit">kcal</span>
              </m.div>

              {/*
                De qué porción habla el número. Sin esto, «620 kcal» flota: no se sabe si es lo
                declarado, una porción del envase o lo que el modelo vio. RN-05 escala a lo
                declarado cuando existe; cuando no, la porción es la que estimó el modelo.
              */}
              <span className="nano muted numeric">
                {item.quantity !== null && item.unit !== null
                  ? `Datos por ${formatGrams(item.quantity)} ${unitLabel(item.unit)} declarados`
                  : estimate.source === 'Vision'
                    ? 'Datos por la porción que el modelo estimó en la foto'
                    : estimate.source === 'Declaration'
                      ? 'Datos por la porción que el modelo dedujo de lo que escribiste'
                      : null}
              </span>

              {isAiEstimate(estimate.source) ? (
                <AiEstimateNotice estimate={estimate} source={estimate.source} />
              ) : (
                isUncertain(estimate) && (
                  <p className="micro muted">
                    {/* Sin el centro de la banda: RF-24 no admite mostrar el valor puntual de una
                        estimación bajo umbral, y la frase dice lo mismo sin él. */}
                    Es una banda de ±25 %. Confirmá o corregí y pasa a ser un número.
                  </p>
                )
              )}

              <MacroGrid
                proteinG={estimate.macros.proteinG}
                carbohydratesG={estimate.macros.carbohydratesG}
                fatG={estimate.macros.fatG}
              />

              {/*
                Fibra y sodio sólo cuando la fuente los declaró. El motor los deja en null si
                nadie los sabía — mostrarlos en cero afirmaría que el alimento no tiene, que es
                otra cosa.
              */}
              {(estimate.macros.fiberG !== null || estimate.macros.sodiumMg !== null) && (
                <p className="micro muted numeric">
                  {[
                    estimate.macros.fiberG !== null
                      ? `Fibra ${formatGrams(estimate.macros.fiberG)} g`
                      : null,
                    estimate.macros.sodiumMg !== null
                      ? `Sodio ${formatGrams(estimate.macros.sodiumMg)} mg`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </>
          )}
        </Card>
      </div>

      {/*
        La tira sólo aparece cuando hay más de una: con una sola, el héroe ya la muestra y
        repetirla sería ruido.
      */}
      {dishes.length > 1 && (
        <Card size="tight">
          <Eyebrow>Fotos del plato</Eyebrow>
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {dishes.map((media, index) => (
              <div key={media.id} style={{ position: 'relative' }}>
                <ItemThumb
                  mealId={mealId}
                  itemId={itemId}
                  media={media}
                  ean={null}
                  alt={`Foto ${index + 1} de ${dishes.length}`}
                />
                <button
                  type="button"
                  className="thumb-remove"
                  aria-label={`Quitar la foto ${index + 1}`}
                  disabled={busy}
                  onClick={() => void run(() => mealsApi.removeImage(mealId, itemId, media.id))}
                >
                  <CloseIcon size={12} />
                </button>
              </div>
            ))}
          </div>
          <span className="micro muted">
            Más de una vista ayuda: de arriba y de costado se estima mejor la porción.
          </span>
        </Card>
      )}

      {item.state === 'Failed' && (
        <Banner tone="decision">
          Se cortó del lado nuestro, no del tuyo: el servicio de estimación no respondió. Se
          reintenta solo; también podés forzarlo ahora.
        </Banner>
      )}

      {error !== null && <Banner tone="danger">{error}</Banner>}

      {/* CU-06 · las tres formas de corregir, cada una respondiendo a una pregunta distinta. */}
      <Card size="flush">
        <button type="button" className="option-row" onClick={() => setCorrecting('quantity')}>
          <span className="stack" style={{ flex: 1, gap: 2 }}>
            <span className="option-title">Cambiar la cantidad</span>
            <span className="option-hint">Los macros se reescalan en la misma proporción.</span>
          </span>
        </button>
        <button type="button" className="option-row" onClick={() => setCorrecting('product')}>
          <span className="stack" style={{ flex: 1, gap: 2 }}>
            <span className="option-title">Cambiar el producto</span>
            <span className="option-hint">
              Buscá el exacto en el catálogo y la estimación sale de ahí.
            </span>
          </span>
        </button>
        <button type="button" className="option-row" onClick={() => setCorrecting('macros')}>
          <span className="stack" style={{ flex: 1, gap: 2 }}>
            <span className="option-title">Editar los macros a mano</span>
            <span className="option-hint">Las kcal se recalculan por Atwater.</span>
          </span>
        </button>
        {hasLabelPhoto && (
          <button
            type="button"
            className="option-row"
            onClick={() => navigate(`/comidas/${mealId}/items/${itemId}/rotulo`)}
          >
            <span className="stack" style={{ flex: 1, gap: 2 }}>
              <span className="option-title">Cargar los valores del rótulo</span>
              <span className="option-hint">Lo que se pudo leer viene precargado.</span>
            </span>
          </button>
        )}
      </Card>

      <EstimateHistory estimates={item.estimates} />

      <div className="action-bar">
        {estimate !== null && item.state === 'NeedsConfirmation' ? (
          <Button
            variant="primary"
            style={{ flex: 1.6 }}
            disabled={busy}
            onClick={() => void run(() => mealsApi.confirm(mealId, itemId))}
          >
            {/* Sin número, por RF-24. El mismo motivo que en el resumen del día: la banda ya está
                en la cifra grande de arriba, y el punto aparece al confirmar. */}
            Confirmar la estimación
          </Button>
        ) : (
          <Button variant="primary" style={{ flex: 1.6 }} onClick={() => navigate(-1)}>
            Listo
          </Button>
        )}
        <Button style={{ flex: 1 }} disabled={busy} onClick={() => setCorrecting('quantity')}>
          Corregir
        </Button>
      </div>

      {/*
        Sin `capture="environment"`. Ese atributo obliga al sistema operativo a abrir la cámara y
        le esconde la galería: quien ya tenía la foto sacada no tenía forma de usarla. Sin él, el
        selector ofrece las dos cosas y la persona elige.
      */}
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = '';

          if (files.length === 0) {
            return;
          }

          // Secuencial: cada subida vuelve a disparar la resolución del ítem, y en paralelo
          // competirían varias pasadas de la cascada por el mismo ítem.
          void run(async () => {
            let latest = meal;
            for (const file of files) {
              latest = await mealsApi.attachImage(mealId, itemId, file, 'Dish');
            }
            return latest;
          });
        }}
      />

      {/*
        RF-14 · borrar el ítem. Existía en la API y no había manera de llegar desde acá, que es
        justo donde se nota el error: estás mirando el ítem que cargaste mal.

        Dos pasos y en rojo, como la comida: se van las estimaciones y las fotos del ítem, y no
        se puede deshacer. Es de los pocos lugares del producto donde se pierde algo de verdad.
      */}
      {borrando ? (
        <Card>
          <Banner tone="danger">
            Se van a borrar este ítem, sus fotos y sus estimaciones. No se puede deshacer.
          </Banner>
          <div className="row">
            <Button variant="danger" disabled={busy} onClick={() => void borrarItem()}>
              Borrar igual
            </Button>
            <Button variant="quiet" onClick={() => setBorrando(false)}>
              Cancelar
            </Button>
          </div>
        </Card>
      ) : (
        <Button variant="plain" onClick={() => setBorrando(true)}>
          Borrar este ítem
        </Button>
      )}

      {correcting !== null && (
        <CorrectItemSheet
          mealId={mealId}
          item={item}
          initialMode={correcting}
          onDone={setMeal}
          onClose={() => setCorrecting(null)}
        />
      )}
    </>
  );
}

/**
 * RN-22 · la declaración de que esto lo estimó un modelo, y qué hacer si no alcanza.
 *
 * Va junto al número y no en un ícono ni en un pie de página: la persona que mira 620 kcal tiene
 * que enterarse ahí mismo de que nadie pesó nada. La advertencia es accionable a propósito —
 * "es aproximado" no le dice a nadie qué hacer al respecto.
 */
function AiEstimateNotice({
  estimate,
  source,
}: {
  estimate: Estimate;
  source: EstimateSource;
}) {
  /*
   * El detalle viene compuesto del motor: la primera línea es la comida entera y cada línea
   * siguiente, un alimento con los gramos que el modelo estimó. El formato es contrato con
   * AnalysisComposer.ComposeDetail — acá se parte, no se interpreta.
   */
  const [description, ...components] = (estimate.detail ?? '').split('\n');

  return (
    <div className="ai-notice stack stack-tight">
      <span className="ai-notice-tag">Analizada por IA</span>
      {description !== undefined && description !== '' && (
        <span className="micro">{description}</span>
      )}

      {components.length > 0 && (
        <div className="stack" style={{ gap: 2 }}>
          <span className="nano muted" style={{ fontWeight: 700 }}>
            Qué hay en tu comida
          </span>
          {components.map((component) => (
            <span key={component} className="micro numeric">
              · {component}
            </span>
          ))}
          <span className="nano muted">
            {source === 'Vision'
              ? 'Los gramos son los que el modelo vio en la foto, no los que declaraste.'
              : 'Los gramos son los que el modelo dedujo de tu descripción.'}
          </span>
        </div>
      )}

      <TraceSteps steps={estimate.trace} />

      <span className="micro muted">
        {/* Ídem: la banda ya está en la cifra grande; repetir su centro la contradice. */}
        Es una banda de ±25 %. {AI_ESTIMATE_ADVICE}
      </span>
    </div>
  );
}

/**
 * Un ítem sin estimación no dice "no se pudo procesar": enumera las tres cosas que le faltan, con
 * la acción de cada una. Un mensaje genérico deja a la persona sin saber qué hacer con su comida.
 *
 * «Todavía no» y «no se pudo» se separan porque piden cosas distintas: en el primer caso lo
 * correcto es esperar, y ofrecer las tres acciones ahí sería pedirle a la persona que arregle algo
 * que no está roto.
 */
function MissingEstimate({
  item,
  busy,
  hasLabelPhoto,
  onResolve,
  onPhoto,
  onProduct,
  onLabel,
}: {
  item: MealItem;
  busy: boolean;
  hasLabelPhoto: boolean;
  onResolve: () => void;
  onPhoto: () => void;
  onProduct: () => void;
  onLabel: () => void;
}) {
  return (
    <>
      <div className="figure figure-item">
        <span className="figure-value uncertain">—</span>
        <span className="figure-unit">{itemStateLabel(item.state)}</span>
      </div>

      {item.state === 'Pending' ? (
        <>
          {/*
            Esqueletos con la forma del resultado: la cifra grande y los tres macros. Anticipan
            qué va a aparecer sin afirmar nada — y a propósito no hay un porcentaje de progreso:
            la resolución no reporta avance, y un «65 %» inventado sería exactamente la clase de
            número fabricado que este producto no muestra.
          */}
          <div className="stack" style={{ gap: 'var(--space-2)' }} aria-hidden="true">
            <span className="skeleton" style={{ width: '38%', height: 34 }} />
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <span className="skeleton" style={{ flex: 1, height: 44 }} />
              <span className="skeleton" style={{ flex: 1, height: 44 }} />
              <span className="skeleton" style={{ flex: 1, height: 44 }} />
            </div>
          </div>
          <p className="micro muted" role="status">
            La estimación se calcula aparte y tarda unos segundos. Esta pantalla se actualiza sola
            cuando esté lista.
          </p>
        </>
      ) : hasLabelPhoto ? (
        /*
         * Hay foto de rótulo: entonces ya sabemos exactamente qué falta, y no es «alguna de estas
         * tres cosas».
         *
         * Antes se mostraba el mismo mensaje genérico de fracaso y las tres acciones de siempre,
         * ninguna de las cuales era cargar el rótulo — esa opción vivía dos tarjetas más abajo,
         * última, entre las correcciones. O sea que a quien acababa de fotografiar un rótulo se le
         * decía que nadie pudo y se le ofrecía sacar otra foto. Tocaba «Reintentar ahora», no
         * pasaba nada, y la conclusión razonable era que el rótulo no funciona.
         */
        <>
          <p className="micro muted">
            La foto del rótulo está, pero de ahí no salieron los valores. Cargalos una vez —lo que
            se haya podido leer viene precargado— y la estimación sale de ahí.
          </p>

          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <Button variant="primary" block onClick={onLabel}>
              Cargar los valores del rótulo
            </Button>
            <Button variant="quiet" block onClick={onProduct}>
              O elegir el producto del catálogo
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="micro muted">
            Ningún resolutor pudo con lo que hay. Cualquiera de estas tres lo destraba:
          </p>

          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <Button variant="quiet" block onClick={onPhoto}>
              Sacarle una foto
            </Button>
            <Button variant="quiet" block onClick={onProduct}>
              Elegir el producto del catálogo
            </Button>
            <Button variant="quiet" block disabled={busy} onClick={onResolve}>
              Reintentar ahora
            </Button>
          </div>
        </>
      )}
    </>
  );
}

/** Posiciones fijas de los destellos, en porcentaje del héroe. Fijas y no aleatorias: el render
    es determinista y dos aperturas de la misma pantalla se ven igual. */
const SCAN_SPARKS: ReadonlyArray<{ left: number; top: number; delay: number }> = [
  { left: 18, top: 30, delay: 0 },
  { left: 62, top: 22, delay: 0.35 },
  { left: 80, top: 55, delay: 0.7 },
  { left: 35, top: 68, delay: 1.05 },
  { left: 50, top: 42, delay: 1.4 },
  { left: 25, top: 82, delay: 1.75 },
  { left: 72, top: 78, delay: 2.1 },
  { left: 44, top: 15, delay: 2.45 },
];

/**
 * Los destellos sobre la foto mientras el ítem está pendiente.
 *
 * Es animación de CSS a propósito: `prefers-reduced-motion` la apaga por el media query global,
 * sin código extra.
 */
function ScanOverlay() {
  return (
    <span className="scan-overlay" aria-hidden="true">
      {SCAN_SPARKS.map((spark) => (
        <span
          key={`${spark.left}-${spark.top}`}
          className="scan-spark"
          style={{
            left: `${spark.left}%`,
            top: `${spark.top}%`,
            animationDelay: `${spark.delay}s`,
          }}
        />
      ))}
    </span>
  );
}

/** Cómo se titula cada clase de paso. El tipo decide el rótulo; el texto lo escribe el motor. */
const TRACE_LABELS: Record<TraceStep['kind'], string> = {
  Declaration: 'Lo que declaraste',
  Search: 'Búsqueda',
  Match: 'De dónde salieron los datos',
  Research: 'Investigado en la web',
  Calculation: 'Cuenta',
  Review: 'Revisión',
};

/**
 * RN-27 · de dónde salió cada número, paso por paso.
 *
 * Va plegado. Es información de auditoría: casi nadie la necesita casi nunca, y desplegada empuja
 * hacia abajo lo que la persona sí vino a ver. Pero cuando un número no cierra, esto es lo único que
 * permite entender por qué — y entonces tiene que estar, no haber sido resumido.
 *
 * El enlace se abre en otra pestaña y con `noreferrer`: la fuente es una página que eligió un
 * modelo, y no hay motivo para contarle de dónde viene quien la abre.
 */
function TraceSteps({ steps }: { steps: TraceStep[] }) {
  if (steps.length === 0) {
    return null;
  }

  return (
    <details className="trace">
      <summary className="nano">Cómo se calculó ({steps.length} pasos)</summary>
      <ol className="trace-steps">
        {steps.map((step, index) => (
          <li key={`${step.kind}-${index}`} className="trace-step">
            <span className="nano muted">{TRACE_LABELS[step.kind]}</span>
            <span className="micro" style={{ fontWeight: 600 }}>
              {step.title}
            </span>
            <span className="micro muted">{step.detail}</span>
            {step.sourceUrl !== null && (
              <a
                className="nano trace-source"
                href={step.sourceUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                Ver la fuente
              </a>
            )}
          </li>
        ))}
      </ol>
    </details>
  );
}
